import { Button } from '@mui/material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import Form from 'src/app/common/components/Form';
import { createNewTypeOfView, modifyTypeOfView } from 'src/app/common/network/typeOfViewCrud';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { ETypeOfView, ITypeOfView } from 'src/app/common/types/view-types';
import { Translation } from 'src/app/i18n';
import { makeStyles } from 'tss-react/mui';
import { addSpecialDays, editSpecialDays, getSpecificDayDetail } from '../../../../network/specialDayCrud';
import type { SpecialDayItem } from '../../../../types/special-day-type';
import type { SpecialDayFormValues } from '../Create/form';
import { SpecialDayForm } from '../Create/form';
import { specialDayPath } from '../SpecialDayPage';

type SpecialDayDetailPageProps = RouteComponentProps<{ id?: string }>;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  field: {
    fontSize: '1rem',
  },
  mandatory: {
    color: 'red',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
  },
  loadingText: {
    padding: '20px 0',
  },
}));

const isFormValidationError = (error: unknown) => {
  return Boolean((error as { errorFields?: unknown[] })?.errorFields);
};

const initialFormValues: SpecialDayFormValues = {
  name: '',
  startDate: undefined,
  endDate: undefined,
  reminder: false,
  daysBeforeEvent: undefined,
  reminderNotification: undefined,
  eventNotification: undefined,
  receiverTypes: [],
  channels: [],
  roles: [],
  status: 'Unpublished',
};

const normalizeInitialValues = (item: SpecialDayItem): SpecialDayFormValues => ({
  name: item.name || '',
  startDate: item.startDate ? new Date(item.startDate) : undefined,
  endDate: item.endDate ? new Date(item.endDate) : undefined,
  reminder: item.reminder || false,
  daysBeforeEvent: Number(item?.daysBeforeEvent ?? 0) > 0 ? item.daysBeforeEvent : undefined,
  reminderNotification: item.reminderNotification,
  eventNotification: item.eventNotification,
  receiverTypes: [
    ...(item.channels?.length ? ['Channel'] : []),
    ...(item.roles?.length ? ['Roles'] : []),
    ...(item.groupOfUsers?.userFile ? [ETypeOfView.GroupOfUsers] : []),
  ],
  channels: item.channels || [],
  roles: item.roles || [],
  status: item.status || 'Unpublished',
  groupOfUsers: item.groupOfUsers?.userFile ? item.groupOfUsers : undefined,
  userFile: item.groupOfUsers?.userFile || null,
});

const SpecialDayDetailPage: FC<SpecialDayDetailPageProps> = ({ match, location }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [form] = Form.useForm<SpecialDayFormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState<SpecialDayFormValues>(initialFormValues);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const updateId = match.params.id;
  const refId = queryParams.get('ref') || undefined;
  const isUpdateMode = Boolean(updateId);
  const isCloneMode = !isUpdateMode && Boolean(refId);
  const sourceId = updateId || refId;
  const title = isUpdateMode ? 'Edit Card' : 'Create New Card';

  useEffect(() => {
    if (!sourceId) {
      form.resetFields();
      form.setFieldsValue(initialFormValues);
      setFormInitialValues(initialFormValues);
      return;
    }

    let isMounted = true;
    const fetchSpecificDay = async () => {
      setIsLoading(true);
      try {
        const data = await getSpecificDayDetail(sourceId);
        if (!isMounted || !data) {
          return;
        }

        const nextInitialValues = normalizeInitialValues(data);
        const values: SpecialDayFormValues = isCloneMode
          ? { ...nextInitialValues, status: 'Unpublished' }
          : nextInitialValues;
        setFormInitialValues(values);
        form.setFieldsValue(values);
      } catch (error) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: Translation('global.submit.fail'),
              content: Translation('global.submit.fail'),
            },
          ]),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSpecificDay();

    return () => {
      isMounted = false;
    };
  }, [form, isCloneMode, sourceId]);

  const submitSpecialDay = async (status: SpecialDayItem['status']) => {
    setIsSubmitting(true);
    try {
      const values = (await form.validateFields()) as SpecialDayFormValues;
      const { receiverTypes, userFile, groupOfUsers, ...formValues } = values;
      const isGroupOfUsersSelected = receiverTypes?.includes(ETypeOfView.GroupOfUsers) ?? false;
      let resolvedGroupOfUsers: ITypeOfView | Record<string, unknown> = {};

      if (isGroupOfUsersSelected) {
        const typeOfViewData: ITypeOfView = {
          type: ETypeOfView.GroupOfUsers,
          userFile,
        };

        resolvedGroupOfUsers = groupOfUsers?.id
          ? await modifyTypeOfView(groupOfUsers.id, typeOfViewData, dispatch)
          : await createNewTypeOfView(typeOfViewData, dispatch);
      }

      const payload = {
        ...formValues,
        name: values.name?.trim(),
        reminderNotification: {
          titleMsg: values.reminderNotification?.titleMsg?.trim(),
          contentMsg: values.reminderNotification?.contentMsg?.trim(),
        },
        eventNotification: {
          titleMsg: values.eventNotification?.titleMsg?.trim(),
          contentMsg: values.eventNotification?.contentMsg?.trim(),
        },
        status,
        startDate: values.startDate?.toISOString(),
        endDate: values.endDate?.toISOString(),
        groupOfUsers: resolvedGroupOfUsers,
      };

      if (isUpdateMode) {
        await editSpecialDays({ ...payload, specialDayId: updateId });
      } else {
        await addSpecialDays(payload);
      }

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('global.submit.success'),
            content: Translation('global.submit.success'),
          },
        ]),
      );
      history.push(`${specialDayPath}/list`);
    } catch (error: any) {
      if (isFormValidationError(error)) {
        return;
      }

      const code = error?.errors?.code || '';
      if (code === 'E011') {
        // Handle specific error code E011 to duplicate
        return form.setFields([
          {
            name: 'name',
            errors: ['A Special Day with the same name already exists'],
          },
        ]);
      }
      return dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: 'Failed, please try again.',
          },
        ]),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveDraft = () => {
    submitSpecialDay('Unpublished');
  };

  const publish = () => {
    submitSpecialDay('Published');
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>{title}</div>
        </div>
        <Button variant="contained" color="inherit" onClick={() => history.push(`${specialDayPath}/list`)}>
          {Translation('app.button.back')}
        </Button>
      </div>

      {isLoading ? (
        <div className={classes.loadingText}>Loading...</div>
      ) : (
        <SpecialDayForm form={form} initialValues={formInitialValues} />
      )}

      <div className={classes.footerContainer}>
        <Button variant="contained" color="inherit" onClick={() => history.push(`${specialDayPath}/list`)}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" disabled={isSubmitting || isLoading} onClick={saveDraft}>
          Save draft
        </Button>
        <Button variant="contained" color="secondary" disabled={isSubmitting || isLoading} onClick={publish}>
          Publish
        </Button>
      </div>
    </div>
  );
};

export default SpecialDayDetailPage;
